import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Banknote,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock,
  Loader2,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import RouletteWheel from "./RouletteWheel";
import { formatCasinoCoin, getCasinoActionErrorMessage } from "../../utils/casinoHelpers";
import { formatAppDateTime } from "../../utils/appTimeFormat";
import {
  buildPresetOutcomes,
  formatOutcomeLabel,
  MIN_CASINO_ALLOCATION,
  OUTCOME_COLORS,
  OUTCOME_GROUPS,
  OUTCOME_PRESETS,
  SUGGESTED_MULTIPLIERS,
} from "../../utils/rouletteOutcomes";
import "../../bond-spin-create.css";

const STEPS = [
  {
    title: "Table limits",
    subtitle: "Pick bet types & set multipliers",
    icon: CircleDot,
  },
  {
    title: "Prize pool",
    subtitle: "Fund payouts & close time",
    icon: Banknote,
  },
  {
    title: "Invites",
    subtitle: "Optional friend codes",
    icon: Users,
  },
];

const QUICK_AMOUNTS = [5000, 10000, 25000, 50000];

const QUICK_DEADLINES = [
  { label: "30m", minutes: 30 },
  { label: "1h", minutes: 60 },
  { label: "3h", minutes: 180 },
  { label: "12h", minutes: 720 },
  { label: "24h", minutes: 1440 },
];

const addMinutes = (date, minutes) => new Date(new Date(date).getTime() + minutes * 60 * 1000);

const toLocalInputValue = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDeadlinePreview = (date) => formatAppDateTime(date);

const CreateBondSpinModal = ({
  open,
  onClose,
  onSubmit,
  userCoinBalance = 0,
  submitting = false,
}) => {
  const [step, setStep] = useState(1);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [allocatedAmount, setAllocatedAmount] = useState(String(MIN_CASINO_ALLOCATION));
  const [deadline, setDeadline] = useState(() => addMinutes(new Date(), 60));
  const [selectedQuickMinutes, setSelectedQuickMinutes] = useState(60);
  const [invites, setInvites] = useState("");
  const [error, setError] = useState("");

  const resetForm = useCallback(() => {
    setStep(1);
    setSelectedOutcomes([]);
    setAllocatedAmount(String(MIN_CASINO_ALLOCATION));
    setDeadline(addMinutes(new Date(), 60));
    setSelectedQuickMinutes(60);
    setInvites("");
    setError("");
  }, []);

  useEffect(() => {
    if (open) return;
    resetForm();
  }, [open, resetForm]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) handleClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, handleClose, submitting]);

  const numericAmount = Math.floor(Number(allocatedAmount) || 0);
  const earliestDeadline = useMemo(() => addMinutes(new Date(), 1), []);
  const canAfford = numericAmount <= userCoinBalance && numericAmount >= MIN_CASINO_ALLOCATION;

  const inviteList = useMemo(
    () =>
      String(invites || "")
        .split(",")
        .map((i) => i.trim())
        .filter(Boolean),
    [invites]
  );

  const toggleOutcome = (outcomeType) => {
    setSelectedOutcomes((prev) => {
      const exists = prev.find((o) => o.outcomeType === outcomeType);
      if (exists) return prev.filter((o) => o.outcomeType !== outcomeType);
      return [
        ...prev,
        { outcomeType, multiplier: String(SUGGESTED_MULTIPLIERS[outcomeType] ?? 2) },
      ];
    });
    setError("");
  };

  const updateMultiplier = (outcomeType, value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    setSelectedOutcomes((prev) =>
      prev.map((o) => (o.outcomeType === outcomeType ? { ...o, multiplier: cleaned } : o))
    );
    setError("");
  };

  const applyPreset = (preset) => {
    setSelectedOutcomes(buildPresetOutcomes(preset.outcomes));
    setError("");
  };

  const validateStep = () => {
    if (step === 1) {
      if (!selectedOutcomes.length) {
        setError("Select at least one bet type for your table.");
        return false;
      }
      for (const outcome of selectedOutcomes) {
        const mult = parseFloat(outcome.multiplier);
        if (!Number.isFinite(mult) || mult < 1) {
          setError(`Enter a valid multiplier (≥ 1) for ${formatOutcomeLabel(outcome.outcomeType)}.`);
          return false;
        }
      }
      setError("");
      return true;
    }

    if (step === 2) {
      if (!numericAmount || numericAmount < MIN_CASINO_ALLOCATION) {
        setError(`Minimum prize pool is ${formatCasinoCoin(MIN_CASINO_ALLOCATION)} COIN.`);
        return false;
      }
      if (numericAmount > userCoinBalance) {
        setError("You do not have enough COIN to fund this table.");
        return false;
      }
      if (deadline.getTime() < earliestDeadline.getTime() - 1000) {
        setError("Deadline must be at least 1 minute from now.");
        return false;
      }
      setError("");
      return true;
    }

    setError("");
    return true;
  };

  const handleNext = () => {
    if (submitting) return;
    if (!validateStep()) return;
    if (step < 3) {
      setStep((s) => s + 1);
      return;
    }
    handleSubmit();
  };

  const handleBack = () => {
    if (submitting || step <= 1) return;
    setStep((s) => s - 1);
    setError("");
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validateStep()) return;

    const outcomes = selectedOutcomes.map((o) => ({
      outcomeType: o.outcomeType,
      multiplier: parseFloat(o.multiplier),
    }));

    try {
      await onSubmit({
        gameType: "roulette",
        allocatedAmount: numericAmount,
        deadline: deadline.toISOString(),
        outcomes,
        invites: inviteList,
      });
      handleClose();
    } catch (e) {
      setError(getCasinoActionErrorMessage(e, "Could not create table. Please try again."));
    }
  };

  const handleQuickDeadline = (minutes) => {
    setDeadline(addMinutes(new Date(), minutes));
    setSelectedQuickMinutes(minutes);
    setError("");
  };

  if (!open) return null;

  const StepIcon = STEPS[step - 1].icon;

  return createPortal(
    <div className="bsc-root" role="dialog" aria-modal="true" aria-labelledby="bsc-title">
      <button
        type="button"
        className="bsc-backdrop"
        aria-label="Close"
        onClick={handleClose}
        disabled={submitting}
      />

      <div className="bsc-sheet" onClick={(e) => e.stopPropagation()}>
        <aside className="bsc-rail">
          <div className="bsc-rail-wheel">
            <RouletteWheel size={112} accent="#64B5F6" idleSpin={false} />
          </div>
          <p className="bsc-rail-kicker">Bond Spin</p>
          <h2 id="bsc-title" className="bsc-rail-title">
            Host a table
          </h2>
          <p className="bsc-rail-copy">
            Set the bets players can take, seed the prize pool in COIN, and open your roulette to the lobby.
          </p>

          <ol className="bsc-step-list">
            {STEPS.map((s, index) => {
              const done = step > index + 1;
              const active = step === index + 1;
              return (
                <li key={s.title} className={`bsc-step-item ${done ? "done" : ""} ${active ? "active" : ""}`}>
                  <span className="bsc-step-badge" aria-hidden>
                    {done ? <Check className="bsc-step-check" size={14} strokeWidth={2.75} /> : index + 1}
                  </span>
                  <div>
                    <strong>{s.title}</strong>
                    <span>{s.subtitle}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </aside>

        <div className="bsc-main">
          <header className="bsc-head">
            <div className="bsc-head-main">
              <span className="bsc-head-icon">
                <StepIcon size={17} />
              </span>
              <div>
                <p className="bsc-head-kicker">Step {step} of 3</p>
                <h3>{STEPS[step - 1].title}</h3>
              </div>
            </div>
            <div className="bsc-head-balance">
              <Banknote size={12} />
              <strong>{formatCasinoCoin(userCoinBalance)} COIN</strong>
            </div>
            <button type="button" className="bsc-close" onClick={handleClose} disabled={submitting} aria-label="Close">
              <X size={16} />
            </button>
          </header>

          <div className="bsc-body">
            {error ? (
              <div className="bsc-error" role="alert">
                {error}
              </div>
            ) : null}

            {step === 1 ? (
              <>
                <div className="bsc-presets">
                  <span className="bsc-presets-label">
                    <Sparkles size={13} />
                    Quick layouts
                  </span>
                  <div className="bsc-preset-row">
                    {OUTCOME_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        className="bsc-preset-btn"
                        onClick={() => applyPreset(preset)}
                      >
                        <strong>{preset.label}</strong>
                        <span>{preset.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bsc-outcome-layout">
                  <div className="bsc-outcome-groups">
                    {OUTCOME_GROUPS.map((group) => (
                      <section key={group.id} className="bsc-outcome-group">
                        <h4>{group.label}</h4>
                        <div className="bsc-outcome-grid">
                          {group.outcomes.map((outcomeType) => {
                            const selected = selectedOutcomes.find((o) => o.outcomeType === outcomeType);
                            const dot = OUTCOME_COLORS[outcomeType] || "#64B5F6";
                            return (
                              <button
                                key={outcomeType}
                                type="button"
                                className={`bsc-outcome-card ${selected ? "selected" : ""}`}
                                onClick={() => toggleOutcome(outcomeType)}
                              >
                                <span className="bsc-outcome-dot" style={{ backgroundColor: dot }} />
                                <span className="bsc-outcome-name">{formatOutcomeLabel(outcomeType)}</span>
                                {selected ? <Check size={14} className="bsc-outcome-check" /> : null}
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>

                  <aside className="bsc-selected-panel">
                    <div className="bsc-selected-head">
                      <strong>{selectedOutcomes.length}</strong>
                      <span>active bets</span>
                    </div>
                    {selectedOutcomes.length === 0 ? (
                      <p className="bsc-selected-empty">Tap bet types on the left to build your table.</p>
                    ) : (
                      <ul className="bsc-selected-list">
                        {selectedOutcomes.map((item) => (
                          <li key={item.outcomeType}>
                            <span>{formatOutcomeLabel(item.outcomeType)}</span>
                            <label className="bsc-mult-field">
                              <span>×</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={item.multiplier}
                                onChange={(e) => updateMultiplier(item.outcomeType, e.target.value)}
                                aria-label={`Multiplier for ${formatOutcomeLabel(item.outcomeType)}`}
                              />
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                  </aside>
                </div>
              </>
            ) : null}

            {step === 2 ? (
              <div className="bsc-pool-layout">
                <section className="bsc-panel">
                  <h4>Prize pool</h4>
                  <p className="bsc-panel-hint">COIN reserved for player payouts. Deducted from your wallet now.</p>
                  <div className="bsc-cash-field">
                    <span className="bsc-coin-badge">COIN</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={allocatedAmount}
                      onChange={(e) => {
                        setAllocatedAmount(e.target.value.replace(/[^0-9]/g, ""));
                        setError("");
                      }}
                      aria-label="Prize pool amount"
                    />
                  </div>
                  <div className="bsc-chip-grid">
                    {QUICK_AMOUNTS.map((amount) => {
                      const active = numericAmount === amount;
                      const disabled = amount > userCoinBalance;
                      return (
                        <button
                          key={amount}
                          type="button"
                          className={`bsc-chip ${active ? "active" : ""}`}
                          disabled={disabled}
                          onClick={() => {
                            setAllocatedAmount(String(amount));
                            setError("");
                          }}
                        >
                          {formatCasinoCoin(amount)}
                        </button>
                      );
                    })}
                  </div>
                  <p className="bsc-footnote">Minimum {formatCasinoCoin(MIN_CASINO_ALLOCATION)} COIN</p>
                </section>

                <section className="bsc-panel">
                  <h4>Close for bets</h4>
                  <p className="bsc-panel-hint">After this time, no new bets — the wheel can spin.</p>
                  <div className="bsc-chip-grid bsc-chip-grid-deadline">
                    {QUICK_DEADLINES.map((d) => (
                      <button
                        key={d.minutes}
                        type="button"
                        className={`bsc-chip ${selectedQuickMinutes === d.minutes ? "active" : ""}`}
                        onClick={() => handleQuickDeadline(d.minutes)}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <label className="bsc-datetime">
                    <span>Custom date & time</span>
                    <input
                      type="datetime-local"
                      value={toLocalInputValue(deadline)}
                      min={toLocalInputValue(earliestDeadline)}
                      onChange={(e) => {
                        const next = new Date(e.target.value);
                        if (Number.isNaN(next.getTime())) return;
                        setDeadline(next);
                        setSelectedQuickMinutes(null);
                        setError("");
                      }}
                    />
                  </label>
                  <div className="bsc-deadline-preview">
                    <Clock size={13} />
                    <span>Closes {formatDeadlinePreview(deadline)}</span>
                  </div>
                </section>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="bsc-invite-layout">
                <section className="bsc-panel bsc-panel-wide">
                  <div className="bsc-invite-head">
                    <h4>Invite codes</h4>
                    <span className="bsc-optional">Optional</span>
                  </div>
                  <p className="bsc-panel-hint">
                    Share friend invite codes so they spot your table faster. The game stays public for everyone.
                  </p>
                  <input
                    className="bsc-invite-input"
                    value={invites}
                    onChange={(e) => {
                      setInvites(e.target.value.toUpperCase());
                      setError("");
                    }}
                    placeholder="CODE123, CODE456"
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                  {inviteList.length > 0 ? (
                    <div className="bsc-invite-chips">
                      {inviteList.map((code) => (
                        <span key={code} className="bsc-invite-chip">
                          {code}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </section>

                <section className="bsc-review">
                  <h4>Table summary</h4>
                  <dl>
                    <div>
                      <dt>Bet types</dt>
                      <dd>{selectedOutcomes.length}</dd>
                    </div>
                    <div>
                      <dt>Prize pool</dt>
                      <dd>{formatCasinoCoin(numericAmount)} COIN</dd>
                    </div>
                    <div>
                      <dt>Closes</dt>
                      <dd>{formatDeadlinePreview(deadline)}</dd>
                    </div>
                    <div>
                      <dt>Invites</dt>
                      <dd>{inviteList.length || "Open lobby"}</dd>
                    </div>
                  </dl>
                </section>
              </div>
            ) : null}
          </div>

          <footer className="bsc-foot">
            <div className="bsc-summary">
              {step === 1 ? (
                <>
                  <span>Selected</span>
                  <strong>{selectedOutcomes.length} bet types</strong>
                </>
              ) : null}
              {step === 2 ? (
                <>
                  <span>Host stake</span>
                  <strong className={canAfford ? "" : "bsc-summary-warn"}>
                    {formatCasinoCoin(numericAmount)} COIN
                  </strong>
                </>
              ) : null}
              {step === 3 ? (
                <>
                  <span>Ready to open</span>
                  <strong>{formatCasinoCoin(numericAmount)} COIN table</strong>
                </>
              ) : null}
            </div>

            <div className="bsc-foot-actions">
              {step > 1 ? (
                <button
                  type="button"
                  className="bsc-btn bsc-btn-ghost bsc-btn-icon"
                  onClick={handleBack}
                  disabled={submitting}
                  aria-label="Back"
                >
                  <ChevronLeft size={18} />
                </button>
              ) : (
                <button type="button" className="bsc-btn bsc-btn-ghost" onClick={handleClose} disabled={submitting}>
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="bsc-btn bsc-btn-primary"
                onClick={handleNext}
                disabled={submitting || (step === 2 && !canAfford)}
              >
                {submitting ? (
                  <>
                    <Loader2 size={15} className="bsc-spin" />
                    Opening…
                  </>
                ) : step < 3 ? (
                  <>
                    Continue
                    <ChevronRight size={16} />
                  </>
                ) : (
                  <>Open table · {formatCasinoCoin(numericAmount)} COIN</>
                )}
              </button>
            </div>
          </footer>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateBondSpinModal;
